// src/components/orgscan/EntrevistaPage.tsx
//
// La entrevista del Diagnóstico Operativo con IA, para el EMPLEADO.
//
// 🚨 2026-07-22 · Por qué existe este archivo:
// El backend habilitaba el módulo `org-scan` con los scopes correctos, pero el
// frontend no tenía módulo con ese `backendModuleKey` → el empleado caía en la
// administración de la empresa y veía "Insufficient permissions". Aquí vive su
// entrevista: reutiliza `ChatPane` (mismo motor que corrió en prod) con el
// encuadre que la persona necesita.
//
// 📐 LAYOUT (2026-07-23) — por qué NO se usa `calc(100vh - Npx)`:
// El contenedor padre `.o-page` es un panel flotante que CRECE con su contenido
// y trae margin+padding con clamp() (variable por breakpoint). Un alto fijo de
// viewport metía un bloque más grande que el hueco real y CORTABA el encabezado
// por arriba. En móvil, además, `100vh` incluye la barra del navegador y miente.
//
// Solución robusta y responsiva:
//   · el encabezado va en FLUJO NATURAL (shrink-0, sin contenedor de alto fijo):
//     es imposible que se corte — siempre queda arriba y, si no cabe, la página
//     hace scroll y sigue alcanzable.
//   · el chat toma un alto relativo a `dvh` (dynamic viewport, correcto en
//     móvil) ACOTADO por min/max, así se adapta de teléfono a monitor sin
//     cálculos frágiles.
// Nada de `overflow-hidden` en el contenedor: si el viewport es diminuto, que
// el usuario pueda hacer scroll en lugar de perder contenido.

import { Clock, Lock } from 'lucide-react';

import { ChatPane } from '@/components/assistant/ChatPane';

// Iniciadores del DIAGNÓSTICO. Los genéricos del asistente ("Resume mis
// pendientes de hoy", "Explícame cómo funciona esta plataforma") desorientan
// aquí: la persona viene a que le pregunten por su trabajo, no a pedirle cosas
// al bot. Estos abren la entrevista o retoman donde se quedó.
const INICIADORES = [
  'Quiero hacer mi diagnóstico operativo',
  'Continuar donde me quedé',
  '¿Qué me falta por contestar?',
  '¿Para qué sirve esto y quién lo ve?',
];

interface EntrevistaPageProps {
  webChatHeaders?: Record<string, string>;
}

export function EntrevistaPage({ webChatHeaders = {} }: EntrevistaPageProps) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 sm:gap-4">
      {/* Encabezado — flujo natural, nunca se corta. Compacto en móvil. */}
      <header className="shrink-0 rounded-xl border border-border bg-card p-4 sm:p-5">
        <h1 className="text-lg font-semibold text-foreground sm:text-xl">
          Tu diagnóstico operativo
        </h1>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:mt-2">
          Una plática con la IA para entender cómo trabajas de verdad.{' '}
          <strong className="text-foreground">No es una evaluación ni un examen.</strong>
        </p>

        <div className="mt-3 flex flex-col gap-1.5 text-xs text-muted-foreground sm:mt-4 sm:flex-row sm:flex-wrap sm:gap-4">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} className="shrink-0" />
            10–15 minutos · puedes pausar y retomar
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Lock size={14} className="shrink-0" />
            Confidencial · la empresa sólo ve el reporte agregado
          </span>
        </div>
      </header>

      {/* Chat — alto responsivo con dynamic-viewport, acotado. Se adapta de
          teléfono (más alto proporcionalmente) a escritorio sin desbordar. */}
      <div className="h-[62dvh] min-h-[360px] max-h-[760px] overflow-hidden rounded-xl">
        <ChatPane
          webChatHeaders={webChatHeaders}
          hideThinking
          emptyTitle="Empecemos cuando quieras"
          emptySubtitle="Escribe abajo o elige por dónde arrancar."
          suggestions={INICIADORES}
          focusTools="org-scan"
          messageActions={false}
          showSteps={false}
        />
      </div>
    </div>
  );
}

export default EntrevistaPage;
